import React from "react";
import { useParams, useNavigate } from "react-router-dom";
import { getCategoryLabel } from "../utils/categoryUtils";
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from "chart.js";
import Layout from "../components/layout/Layout";
import PageHeader from "../components/common/PageHeader";
import SummaryCards from "../components/common/SummaryCards";
import ExpensesListCard from "../components/common/ExpensesListCard";
import ExpenseGroupCard from "../components/common/ExpenseGroupCard";
import ExpenseFilters from "../components/common/ExpenseFilters";
import ExpensesChart from "../components/common/ExpensesChart";
import { useControlCash, CURRENCY_OPTIONS } from "../store/controlCashStore";
import { useToast } from "../contexts/ToastContext";
import { useConfirm } from "../contexts/ConfirmContext";
import { CATEGORY_GRADIENT_COLORS, LEGACY_CATEGORY_OTHER } from "../constants";
import { getSafeArray } from "../utils/common";
import { CurrencyTotals } from "../components/home/CurrencyTotals";
import { formatAmountByCurrency } from "../utils/currency";

ChartJS.register(ArcElement, Tooltip, Legend);

// ============================================================================
// COMPONENTE PRINCIPAL: MonthDetail
// ============================================================================
const MonthDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { state, dispatch, getMonthById, getMonthTotals, getMonthTotalsByCurrency, getMonthExpenses, getMonthConversions, formatDisplayAmount } = useControlCash();
  const toast = useToast();
  const { confirm } = useConfirm();
  const [showRecurringModal, setShowRecurringModal] = React.useState(false);
  const [selectedTemplates, setSelectedTemplates] = React.useState([]);
  const [filterSearch, setFilterSearch] = React.useState("");
  const [filterCategory, setFilterCategory] = React.useState("");

  // Obtener el mes del store
  const month = getMonthById(id);

  // Si no existe el mes, mostrar mensaje
  if (!month) {
    return (
      <Layout>
        <div className="max-w-7xl mx-auto">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-md p-12 text-center">
            <svg className="w-16 h-16 mx-auto mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <h3 className="text-xl font-bold text-gray-800 dark:text-gray-100 mb-2">Mes no encontrado</h3>
            <p className="text-gray-600 dark:text-gray-400 mb-6">El mes que buscas no existe</p>
            <button
              onClick={() => navigate("/")}
              className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-xl shadow-md transition-colors"
            >
              Volver al inicio
            </button>
          </div>
        </div>
      </Layout>
    );
  }

  // Obtener datos del mes
  const totals = getMonthTotals(id);
  const totalsByCurrency = getMonthTotalsByCurrency(id);
  const allExpenses = getMonthExpenses(id);
  const conversions = getMonthConversions(id);

  // Separar gastos pendientes y pagados
  const pendingExpenses = allExpenses.filter(e => e.status === "PENDING");
  const paidExpenses = allExpenses.filter(e => e.status === "PAID");

  // Normalizar categoría (legacy labels para datos antiguos)
  const normalizeCat = (c) => {
    if (!c) return LEGACY_CATEGORY_OTHER;
    if (c === "Gastos Fijos") return "fixed";
    if (c === "Suscripciones") return "subscriptions";
    if (c === "Extras") return "extras";
    return c;
  };

  const customCategories = getSafeArray(state.customCategories);

  // Agrupar gastos por categoría
  const expensesByCategory = {};
  allExpenses.forEach(e => {
    const key = normalizeCat(e.category);
    if (!expensesByCategory[key]) expensesByCategory[key] = [];
    expensesByCategory[key].push(e);
  });

  const getCategoryLabelLocal = (key) => getCategoryLabel(key, customCategories);

  const getCategoryColor = (key) => {
    const idx = customCategories.findIndex(c => c.id === key);
    return CATEGORY_GRADIENT_COLORS[idx >= 0 ? idx % CATEGORY_GRADIENT_COLORS.length : 0];
  };

  const displayCategories = [...customCategories.map(c => c.id), LEGACY_CATEGORY_OTHER].filter(k => (expensesByCategory[k]?.length || 0) > 0);

  // Aplicar filtros a los gastos
  const filterExpenses = (list) => {
    return list.filter((e) => {
      const nameMatch = !filterSearch.trim() || e.name?.toLowerCase().includes(filterSearch.trim().toLowerCase());
      const catKey = normalizeCat(e.category);
      const catMatch = !filterCategory || catKey === filterCategory;
      return nameMatch && catMatch;
    });
  };

  const filteredPendingExpenses = filterExpenses(pendingExpenses);
  const filteredPaidExpenses = filterExpenses(paidExpenses);

  // Gastos por categoría filtrados (para las tarjetas de categoría)
  const filteredExpensesByCategory = {};
  const catsToShow = filterCategory ? [filterCategory] : displayCategories;
  catsToShow.forEach((key) => {
    const list = (expensesByCategory[key] || []).filter((e) => {
      if (!filterSearch.trim()) return true;
      return e.name?.toLowerCase().includes(filterSearch.trim().toLowerCase());
    });
    if (list.length > 0) filteredExpensesByCategory[key] = list;
  });

  const filteredDisplayCategories = Object.keys(filteredExpensesByCategory);

  // Calcular plantillas disponibles para el botón "Seleccionar todos"
  const availableTemplatesForSelection = state.recurringTemplates.filter(t => {
    if (!t.active) return false;
    const existingTemplateIds = allExpenses
      .filter(e => e.isFromRecurring && e.recurringTemplateId)
      .map(e => e.recurringTemplateId);
    return !existingTemplateIds.includes(t.id);
  });
  const allTemplatesSelected = selectedTemplates.length === availableTemplatesForSelection.length && availableTemplatesForSelection.length > 0;

  const handleToggleBillStatus = async (billId) => {
    const expense = allExpenses.find(e => e.id === billId);
    if (!expense || expense.status !== "PENDING") {
      // Si ya está pagado, solo cambiar el estado
      dispatch({
        type: "TOGGLE_EXPENSE_PAID",
        payload: billId
      });
      return;
    }

    // Verificar si hay suficiente balance en la moneda del gasto
    const expenseCurrency = expense.currency || "PEN";
    const expenseAmount = expense.amount || 0;
    const currencyTotals = totalsByCurrency[expenseCurrency];
    
    if (currencyTotals) {
      // Calcular el balance disponible después de pagar este gasto
      const currentBalance = currencyTotals.availableRemaining;
      const balanceAfterPayment = currentBalance - expenseAmount;
      
      // Si el balance quedaría negativo, ofrecer conversión
      if (balanceAfterPayment < 0) {
        // Buscar otras monedas con balance positivo
        const availableCurrencies = Object.keys(totalsByCurrency)
          .filter(curr => {
            const totals = totalsByCurrency[curr];
            return curr !== expenseCurrency && totals.availableRemaining > 0;
          })
          .map(curr => {
            const totals = totalsByCurrency[curr];
            const currencyInfo = CURRENCY_OPTIONS.find(opt => opt.code === curr);
            return {
              code: curr,
              label: currencyInfo?.label || curr,
              symbol: currencyInfo?.symbol || curr,
              available: totals.availableRemaining
            };
          });

        if (availableCurrencies.length > 0) {
          // Calcular cuánto se necesita convertir desde cada moneda disponible
          const exchangeRates = state.exchangeRates || {};
          const conversionOptions = availableCurrencies.map(fromCurr => {
            const fromRate = exchangeRates[fromCurr.code] || 1;
            const toRate = exchangeRates[expenseCurrency] || 1;
            const exchangeRate = toRate / fromRate;
            // Calcular cuánto necesitamos en la moneda origen para obtener el monto del gasto
            const neededInFromCurrency = expenseAmount / exchangeRate;
            
            return {
              ...fromCurr,
              exchangeRate,
              neededAmount: neededInFromCurrency,
              canAfford: fromCurr.available >= neededInFromCurrency
            };
          });

          // Usar la primera moneda que tenga suficiente balance, o la primera disponible
          const selectedOption = conversionOptions.find(opt => opt.canAfford) || conversionOptions[0];
          
          const expenseCurrencyLabel = expenseCurrency === "PEN" ? "soles" : expenseCurrency === "USD" ? "dólares" : expenseCurrency;
          const fromCurrencyLabel = selectedOption.label;
          
          const shouldConvert = await confirm({
            title: "Fondos insuficientes",
            message: `No tienes suficiente balance en ${expenseCurrencyLabel} para pagar este gasto.\n\n` +
              `Balance disponible: ${formatDisplayAmount(currentBalance, false, expenseCurrency)}\n` +
              `Gasto a pagar: ${formatDisplayAmount(expenseAmount, false, expenseCurrency)}\n` +
              `Faltan: ${formatDisplayAmount(Math.abs(balanceAfterPayment), false, expenseCurrency)}\n\n` +
              `¿Quieres convertir ${formatDisplayAmount(selectedOption.neededAmount, false, selectedOption.code)} desde ${fromCurrencyLabel} para pagar este gasto?\n\n` +
              `Tipo de cambio: 1 ${selectedOption.code} = ${selectedOption.exchangeRate.toFixed(4)} ${expenseCurrency}`,
            confirmLabel: "Sí, convertir y pagar",
            cancelLabel: "Cancelar"
          });

          if (shouldConvert) {
            // Crear conversión automática
            const toAmount = expenseAmount; // El monto exacto del gasto
            
            try {
              await dispatch({
                type: "ADD_CURRENCY_CONVERSION",
                payload: {
                  monthId: id,
                  fromCurrency: selectedOption.code,
                  fromAmount: selectedOption.neededAmount,
                  toCurrency: expenseCurrency,
                  toAmount: toAmount,
                  exchangeRate: selectedOption.exchangeRate,
                  date: new Date().toISOString().split("T")[0],
                  note: `Conversión automática para pagar: ${expense.name}`
                }
              });
              
              // Ahora pagar el gasto
              dispatch({
                type: "TOGGLE_EXPENSE_PAID",
                payload: billId
              });
              
              toast.success(`Conversión realizada y gasto pagado exitosamente`);
            } catch (error) {
              console.error("Error al crear conversión:", error);
              toast.error("Error al crear la conversión. Intenta de nuevo.");
            }
            return;
          } else {
            // Usuario canceló, no hacer nada
            return;
          }
        }
      }
    }

    // Si hay suficiente balance o no hay otras monedas disponibles, pagar normalmente
    dispatch({
      type: "TOGGLE_EXPENSE_PAID",
      payload: billId
    });
  };

  const handleAddExpense = () => {
    navigate(`/expense/add?monthId=${id}`);
  };

  const handleEditExpense = (expenseId) => {
    navigate(`/expense/edit/${expenseId}`);
  };

  const handleDeleteExpense = async (expenseId, expenseName) => {
    const ok = await confirm({
      title: "Eliminar gasto",
      message: `¿Estás seguro de eliminar el gasto "${expenseName}"?\n\nEsta acción no se puede deshacer.`,
      confirmLabel: "Eliminar"
    });
    if (ok) {
      dispatch({ type: "DELETE_EXPENSE", payload: expenseId });
    }
  };

  const handleBack = () => {
    navigate("/");
  };

  const handleEditIncomes = () => {
    navigate(`/month/${id}/edit`);
  };

  const handleDeleteMonth = async () => {
    const ok = await confirm({
      title: "Eliminar mes",
      message: `¿Estás seguro de eliminar "${month.name}"?\n\nSe eliminarán también los ingresos y gastos de este mes.`,
      confirmLabel: "Eliminar"
    });
    if (ok) {
      dispatch({ type: "DELETE_MONTH", payload: id });
      navigate("/");
    }
  };

  const handleOpenRecurringModal = () => {
    // Filtrar plantillas activas que no estén ya agregadas al mes
    const existingTemplateIds = allExpenses
      .filter(e => e.isFromRecurring && e.recurringTemplateId)
      .map(e => e.recurringTemplateId);
    
    const availableTemplates = state.recurringTemplates.filter(
      t => t.active && !existingTemplateIds.includes(t.id)
    );

    if (availableTemplates.length === 0) {
      toast.warning("No hay plantillas recurrentes disponibles para agregar.\n\nTodas las plantillas activas ya están en este mes, o no tienes plantillas activas.");
      return;
    }

    setShowRecurringModal(true);
  };

  const handleToggleTemplate = (templateId) => {
    setSelectedTemplates(prev => 
      prev.includes(templateId)
        ? prev.filter(id => id !== templateId)
        : [...prev, templateId]
    );
  };

  const handleSelectAllTemplates = () => {
    const monthExpenses = state.expenses.filter(e => e.monthId === id);
    const availableTemplates = state.recurringTemplates
      .filter(t => {
        // Filtrar solo activas y que no estén ya en el mes
        if (!t.active) return false;
        const existingTemplateIds = monthExpenses
          .filter(e => e.isFromRecurring && e.recurringTemplateId)
          .map(e => e.recurringTemplateId);
        return !existingTemplateIds.includes(t.id);
      })
      .map(t => t.id);

    // Si ya están todos seleccionados, deseleccionar todos
    if (selectedTemplates.length === availableTemplates.length) {
      setSelectedTemplates([]);
    } else {
      // Seleccionar todos
      setSelectedTemplates(availableTemplates);
    }
  };

  const handleAddRecurringExpenses = () => {
    if (selectedTemplates.length === 0) {
      toast.warning("Selecciona al menos una plantilla para agregar");
      return;
    }

    selectedTemplates.forEach(templateId => {
      const template = state.recurringTemplates.find(t => t.id === templateId);
      if (template) {
        dispatch({
          type: "ADD_EXPENSE",
          payload: {
            monthId: id,
            name: template.name,
            amount: template.amount,
            currency: template.currency || "PEN",
            category: template.category,
            status: "PENDING",
            dueDay: template.dueDay,
            isFromRecurring: true,
            recurringTemplateId: template.id,
            remindDaysBefore: template.remindDaysBefore,
            date: new Date().toISOString(),
            paidAt: null
          }
        });
      }
    });

    setShowRecurringModal(false);
    setSelectedTemplates([]);
  };

  return (
    <Layout>
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <PageHeader 
          monthName={month.name} 
          onBack={handleBack} 
          onEditIncomes={handleEditIncomes} 
          onDeleteMonth={handleDeleteMonth}
        />

        {/* Totales por Moneda - Visualización mejorada */}
        {Object.keys(totalsByCurrency).length > 0 && (
          <div className="mb-8">
            <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
              Balance por Moneda
            </h2>
            <CurrencyTotals
              totalsByCurrency={totalsByCurrency}
              exchangeRates={state.exchangeRates || {}}
              baseCurrency={state.currency || "PEN"}
            />
          </div>
        )}



        {/* Resumen del Mes - Solo mostrar si hay una sola moneda o como referencia */}
        {Object.keys(totalsByCurrency).length <= 1 && (
          <div className="mb-8">
            <SummaryCards 
              summary={totals} 
              formatAmount={formatDisplayAmount}
              currency={totals.baseCurrency || state.currency || "PEN"}
            />
          </div>
        )}
        
        {/* Aviso cuando hay múltiples monedas */}
        {Object.keys(totalsByCurrency).length > 1 && (
          <div className="mb-8 bg-blue-50 dark:bg-blue-950/20 border-2 border-blue-200 dark:border-blue-800 rounded-xl p-4">
            <div className="flex items-start gap-3">
              <svg className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div>
                <p className="text-sm font-semibold text-blue-900 dark:text-blue-100 mb-1">
                  Múltiples monedas detectadas
                </p>
                <p className="text-xs text-blue-700 dark:text-blue-300">
                  Este mes tiene transacciones en {Object.keys(totalsByCurrency).length} monedas diferentes.
                  Revisa la sección &quot;Totales por Moneda&quot; arriba para ver los detalles separados por cada moneda.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Botones de acción */}
        <div className="flex items-center justify-end gap-2 mb-6">
          <button
            onClick={handleOpenRecurringModal}
            className="px-4 py-3 bg-purple-600 hover:bg-purple-700 text-white font-semibold rounded-xl shadow-md transition-colors flex items-center gap-2"
            aria-label="Agregar desde plantillas"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            <span className="hidden sm:inline">Desde plantillas</span>
          </button>
          <button
            onClick={handleAddExpense}
            className="px-5 py-3 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-xl shadow-md transition-colors flex items-center gap-2"
            aria-label="Agregar nuevo gasto"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            <span className="hidden sm:inline">Agregar gasto</span>
          </button>
        </div>

        {/* Gastos Pendientes y Pagados - Dos columnas en desktop */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* Gastos Pendientes */}
          <ExpensesListCard
            title="Gastos Pendientes"
            expenses={filteredPendingExpenses}
            formatAmount={(amount, hideValue, currency) => formatDisplayAmount(amount, hideValue, currency)}
            color="from-orange-500 to-red-500"
            icon={
              <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            }
            onEditExpense={handleEditExpense}
            onDeleteExpense={handleDeleteExpense}
            onToggleStatus={handleToggleBillStatus}
            emptyMessage="¡Genial! No tienes gastos pendientes"
          />

          {/* Gastos Pagados */}
          <ExpensesListCard
            title="Gastos Pagados"
            expenses={filteredPaidExpenses}
            formatAmount={(amount, hideValue, currency) => formatDisplayAmount(amount, hideValue, currency)}
            color="from-green-500 to-emerald-600"
            icon={
              <svg className="w-7 h-7 text-white" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
            }
            onEditExpense={handleEditExpense}
            onDeleteExpense={handleDeleteExpense}
            onToggleStatus={handleToggleBillStatus}
            emptyMessage="Aún no has pagado ningún gasto"
          />
        </div>

        {/* Distribución de gastos por moneda - Gráfico de barras horizontal */}
        {Object.keys(totalsByCurrency).length > 1 && (
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md p-6 mb-6">
            <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4">
              Gastos Totales por Moneda
            </h3>
            <div className="space-y-3">
              {Object.keys(totalsByCurrency).sort().map((currency, index) => {
                const totals = totalsByCurrency[currency];
                const currencyInfo = CURRENCY_OPTIONS.find(opt => opt.code === currency);
                const maxAmount = Math.max(...Object.values(totalsByCurrency).map(t => t.totalCommitted));
                const percentage = maxAmount > 0 ? (totals.totalCommitted / maxAmount) * 100 : 0;
                
                // Colores por moneda
                const currencyColors = {
                  PEN: "bg-emerald-500",
                  USD: "bg-blue-500",
                  COP: "bg-yellow-500",
                  ARS: "bg-sky-500",
                  CLP: "bg-red-500",
                  VEF: "bg-purple-500"
                };
                
                const barColor = currencyColors[currency] || "bg-gray-500";
                
                return (
                  <div key={currency} className="space-y-1">
                    <div className="flex items-center justify-between text-sm">
                      <div className="flex items-center gap-2">
                        <div className={`w-3 h-3 rounded-full ${barColor}`}></div>
                        <span className="font-medium text-gray-900 dark:text-white">
                          {currencyInfo?.label || currency}
                        </span>
                      </div>
                      <span className="font-bold text-gray-900 dark:text-white">
                        {formatDisplayAmount(totals.totalCommitted, undefined, currency)}
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-3 overflow-hidden">
                      <div
                        className={`${barColor} h-full rounded-full transition-all duration-500`}
                        style={{ width: `${percentage}%` }}
                      ></div>
                    </div>
                  </div>
                );
              })}
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-4 text-center italic">
              Las barras muestran la proporción relativa entre monedas. Cada moneda se muestra con su monto original.
            </p>
          </div>
        )}

        {/* Separador */}
        <div className="border-t-2 border-gray-200 dark:border-gray-700 my-8"></div>

        {/* Conversiones de Moneda - Al final */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-2">
            <div>
              <h2 className="text-base font-bold text-gray-900 dark:text-white">
                Conversiones de Moneda
              </h2>
            </div>
            <button
              onClick={() => navigate(`/conversion/add?monthId=${id}`)}
              className="px-2.5 py-1 bg-gradient-to-r from-purple-500 to-purple-600 hover:from-purple-600 hover:to-purple-700 text-white text-xs font-semibold rounded-lg shadow-sm hover:shadow transition-all flex items-center gap-1"
              aria-label="Registrar conversión de moneda"
            >
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Nueva
            </button>
          </div>
          {conversions.length === 0 ? (
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-3 text-center border border-dashed border-gray-200 dark:border-gray-700">
              <p className="text-xs text-gray-500 dark:text-gray-400">No hay conversiones registradas</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2.5">
              {conversions.map((conv) => {
                const fromCurrencyInfo = CURRENCY_OPTIONS.find(opt => opt.code === conv.fromCurrency);
                const toCurrencyInfo = CURRENCY_OPTIONS.find(opt => opt.code === conv.toCurrency);
                
                return (
                  <div
                    key={conv.id}
                    className="bg-white dark:bg-gray-800 rounded-lg p-2.5 shadow-sm hover:shadow-md transition-all border border-gray-200 dark:border-gray-700"
                  >
                    <div className="flex items-center justify-between gap-2">
                      {/* De */}
                      <div className="flex items-center gap-1.5 flex-1 min-w-0">
                        <div className="w-6 h-6 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center flex-shrink-0">
                          <span className="text-xs font-bold text-red-600 dark:text-red-400">
                            {fromCurrencyInfo?.symbol || conv.fromCurrency}
                          </span>
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-xs font-bold text-gray-900 dark:text-gray-100 truncate">
                            {formatDisplayAmount(conv.fromAmount, undefined, conv.fromCurrency)}
                          </p>
                        </div>
                      </div>

                      {/* Flecha */}
                      <div className="flex flex-col items-center flex-shrink-0 px-1">
                        <svg className="w-3.5 h-3.5 text-purple-600 dark:text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                        </svg>
                        <span className="text-[10px] text-purple-600 dark:text-purple-400 font-medium mt-0.5 leading-tight">
                          {conv.exchangeRate.toFixed(4)}
                        </span>
                      </div>

                      {/* A */}
                      <div className="flex items-center gap-1.5 flex-1 min-w-0">
                        <div className="w-6 h-6 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center flex-shrink-0">
                          <span className="text-xs font-bold text-green-600 dark:text-green-400">
                            {toCurrencyInfo?.symbol || conv.toCurrency}
                          </span>
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-xs font-bold text-gray-900 dark:text-gray-100 truncate">
                            {formatDisplayAmount(conv.toAmount, undefined, conv.toCurrency)}
                          </p>
                        </div>
                      </div>

                      {/* Acciones */}
                      <div className="flex items-center gap-0.5 flex-shrink-0">
                        <button
                          onClick={() => navigate(`/conversion/edit/${conv.id}`)}
                          className="p-1 text-gray-600 hover:text-purple-600 hover:bg-purple-50 dark:hover:bg-purple-900/20 dark:text-gray-400 dark:hover:text-purple-400 rounded transition-colors"
                          aria-label="Editar conversión"
                        >
                          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                        </button>
                        <button
                          onClick={async () => {
                            const ok = await confirm({
                              title: "Eliminar conversión",
                              message: `¿Estás seguro de eliminar esta conversión?`,
                              confirmLabel: "Eliminar",
                              cancelLabel: "Cancelar"
                            });
                            if (ok) {
                              dispatch({ type: "DELETE_CURRENCY_CONVERSION", payload: conv.id });
                              toast.success("Conversión eliminada");
                            }
                          }}
                          className="p-1 text-gray-600 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 dark:text-gray-400 dark:hover:text-red-400 rounded transition-colors"
                          aria-label="Eliminar conversión"
                        >
                          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Filtros de gastos */}
        <ExpenseFilters
          search={filterSearch}
          setSearch={setFilterSearch}
          filterCategory={filterCategory}
          setFilterCategory={setFilterCategory}
          categoryIds={[...customCategories.map(c => c.id), LEGACY_CATEGORY_OTHER]}
          getCategoryLabel={getCategoryLabelLocal}
        />

        {/* Gastos por Categoría - Agrupados por Moneda */}
        <div className="mb-6">
          <div className="mb-6">
            <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100">Gastos por Categoría</h2>
            <p className="text-gray-600 dark:text-gray-400 text-sm">Vista organizada por tipo de gasto y moneda</p>
          </div>

          {Object.keys(totalsByCurrency).length > 1 ? (
            // Múltiples monedas: mostrar por moneda
            Object.keys(totalsByCurrency).sort().map((currency) => {
              const currencyInfo = CURRENCY_OPTIONS.find(opt => opt.code === currency);
              const currencyExpenses = allExpenses.filter(e => (e.currency || "PEN") === currency);
              
              // Agrupar gastos de esta moneda por categoría
              const expensesByCategoryForCurrency = {};
              currencyExpenses.forEach(e => {
                const key = normalizeCat(e.category);
                if (!expensesByCategoryForCurrency[key]) expensesByCategoryForCurrency[key] = [];
                expensesByCategoryForCurrency[key].push(e);
              });

              // Aplicar filtros
              const filteredExpensesByCategoryForCurrency = {};
              const catsToShowForCurrency = filterCategory ? [filterCategory] : displayCategories;
              catsToShowForCurrency.forEach((key) => {
                const list = (expensesByCategoryForCurrency[key] || []).filter((e) => {
                  if (!filterSearch.trim()) return true;
                  return e.name?.toLowerCase().includes(filterSearch.trim().toLowerCase());
                });
                if (list.length > 0) filteredExpensesByCategoryForCurrency[key] = list;
              });

              const displayCategoriesForCurrency = Object.keys(filteredExpensesByCategoryForCurrency);

              if (displayCategoriesForCurrency.length === 0) return null;

              return (
                <div key={currency} className="mb-8">
                  <div className="flex items-center gap-2 mb-4">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                      currency === "PEN" ? "bg-emerald-100 dark:bg-emerald-900/30" :
                      currency === "USD" ? "bg-blue-100 dark:bg-blue-900/30" :
                      currency === "VEF" ? "bg-purple-100 dark:bg-purple-900/30" :
                      currency === "COP" ? "bg-yellow-100 dark:bg-yellow-900/30" :
                      currency === "ARS" ? "bg-sky-100 dark:bg-sky-900/30" :
                      currency === "CLP" ? "bg-red-100 dark:bg-red-900/30" :
                      "bg-gray-100 dark:bg-gray-700"
                    }`}>
                      <span className={`text-sm font-bold ${
                        currency === "PEN" ? "text-emerald-600 dark:text-emerald-400" :
                        currency === "USD" ? "text-blue-600 dark:text-blue-400" :
                        currency === "VEF" ? "text-purple-600 dark:text-purple-400" :
                        currency === "COP" ? "text-yellow-600 dark:text-yellow-400" :
                        currency === "ARS" ? "text-sky-600 dark:text-sky-400" :
                        currency === "CLP" ? "text-red-600 dark:text-red-400" :
                        "text-gray-600 dark:text-gray-400"
                      }`}>
                        {currencyInfo?.symbol || currency}
                      </span>
                    </div>
                    <h3 className="text-xl font-bold text-gray-800 dark:text-gray-100">
                      {currencyInfo?.label || currency}
                    </h3>
                    <span className="text-sm text-gray-500 dark:text-gray-400">
                      ({displayCategoriesForCurrency.length} {displayCategoriesForCurrency.length === 1 ? 'categoría' : 'categorías'})
                    </span>
                  </div>
                  <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    {displayCategoriesForCurrency.map((key) => (
                      <ExpenseGroupCard
                        key={`${currency}-${key}`}
                        title={getCategoryLabelLocal(key)}
                        expenses={filteredExpensesByCategoryForCurrency[key] || []}
                        formatAmount={(amount, hideValue, currency) => formatDisplayAmount(amount, hideValue, currency)}
                        color={getCategoryColor(key)}
                        onEditExpense={handleEditExpense}
                        onDeleteExpense={handleDeleteExpense}
                        icon={
                          <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                        }
                      />
                    ))}
                  </div>
                </div>
              );
            })
          ) : (
            // Una sola moneda: mostrar normalmente
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {filteredDisplayCategories.map((key) => (
                <ExpenseGroupCard
                  key={key}
                  title={getCategoryLabelLocal(key)}
                  expenses={filteredExpensesByCategory[key] || []}
                  formatAmount={(amount, hideValue, currency) => formatDisplayAmount(amount, hideValue, currency)}
                  color={getCategoryColor(key)}
                  onEditExpense={handleEditExpense}
                  onDeleteExpense={handleDeleteExpense}
                  icon={
                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  }
                />
              ))}
            </div>
          )}
        </div>

        {/* Modal para agregar gastos recurrentes */}
        {showRecurringModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-2xl w-full max-h-[80vh] overflow-hidden">
              {/* Header */}
              <div className="bg-gradient-to-r from-purple-600 to-purple-700 p-6 text-white">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <h3 className="text-2xl font-bold">Agregar Gastos Recurrentes</h3>
                    <p className="text-purple-100 text-sm mt-1">Selecciona las plantillas que quieres agregar a este mes</p>
                  </div>
                  <button
                    onClick={() => {
                      setShowRecurringModal(false);
                      setSelectedTemplates([]);
                    }}
                    className="text-white hover:bg-purple-800 rounded-lg p-2 transition-colors"
                    aria-label="Cerrar"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
                {/* Botón Seleccionar Todos */}
                <button
                  onClick={handleSelectAllTemplates}
                  className="bg-white/20 hover:bg-white/30 text-white px-4 py-2 rounded-lg font-semibold text-sm transition-colors flex items-center gap-2"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    {allTemplatesSelected ? (
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    ) : (
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    )}
                  </svg>
                  {allTemplatesSelected ? "Deseleccionar todos" : "Seleccionar todos"}
                </button>
              </div>

              {/* Lista de plantillas */}
              <div className="p-6 overflow-y-auto max-h-[50vh]">
                {state.recurringTemplates
                  .filter(t => {
                    // Filtrar solo activas y que no estén ya en el mes
                    if (!t.active) return false;
                    const existingTemplateIds = allExpenses
                      .filter(e => e.isFromRecurring && e.recurringTemplateId)
                      .map(e => e.recurringTemplateId);
                    return !existingTemplateIds.includes(t.id);
                  })
                  .map(template => {
                    const isSelected = selectedTemplates.includes(template.id);
                    
                    return (
                      <div
                        key={template.id}
                        onClick={() => handleToggleTemplate(template.id)}
                        className={`mb-3 p-4 rounded-xl border-2 cursor-pointer transition-all ${
                          isSelected
                            ? "border-purple-500 bg-purple-50 dark:bg-purple-900/30"
                            : "border-gray-200 dark:border-gray-600 hover:border-purple-300 dark:hover:border-purple-500 bg-white dark:bg-gray-700"
                        }`}
                      >
                        <div className="flex items-center gap-4">
                          {/* Checkbox */}
                          <div className={`w-6 h-6 rounded-md border-2 flex items-center justify-center transition-colors ${
                            isSelected
                              ? "bg-purple-600 border-purple-600"
                              : "border-gray-300"
                          }`}>
                            {isSelected && (
                              <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                              </svg>
                            )}
                          </div>

                          {/* Info */}
                          <div className="flex-1">
                            <div className="flex items-center justify-between mb-1">
                              <h4 className="font-bold text-lg text-gray-800 dark:text-gray-100">{template.name}</h4>
                              <span className="text-xl font-bold text-purple-600">
                                {formatAmountByCurrency(template.amount, template.currency || "PEN")}
                              </span>
                            </div>
                            <div className="flex items-center gap-4 text-sm text-gray-600 dark:text-gray-400">
                              <span className="bg-gray-100 dark:bg-gray-600 px-2 py-1 rounded">
                                {getCategoryLabel(template.category, customCategories)}
                              </span>
                              <span>Vence día {template.dueDay}</span>
                              {template.remindDaysBefore > 0 && (
                                <span className="text-orange-600">
                                  🔔 {template.remindDaysBefore} {template.remindDaysBefore === 1 ? "día" : "días"} antes
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
              </div>

              {/* Footer */}
              <div className="bg-gray-50 dark:bg-gray-700/50 p-6 border-t border-gray-200 dark:border-gray-600">
                <div className="flex items-center justify-between mb-4">
                  <div className="text-sm text-gray-600">
                    <span className="font-semibold">{selectedTemplates.length}</span> {selectedTemplates.length === 1 ? "plantilla seleccionada" : "plantillas seleccionadas"}
                  </div>
                  {selectedTemplates.length > 0 && (
                    <div className="text-right">
                      <div className="text-sm text-gray-600">Total a agregar:</div>
                      <div className="text-2xl font-bold text-purple-600">
                        {formatAmountByCurrency(
                          selectedTemplates.reduce((sum, tId) => {
                            const t = state.recurringTemplates.find(template => template.id === tId);
                            return sum + (t?.amount || 0);
                          }, 0),
                          "PEN" // Por ahora asumimos PEN para el total, podría mejorarse agrupando por moneda
                        )}
                      </div>
                    </div>
                  )}
                </div>
                <div className="flex gap-3">
                  <button
                    onClick={() => {
                      setShowRecurringModal(false);
                      setSelectedTemplates([]);
                    }}
                    className="flex-1 px-6 py-3 border-2 border-gray-300 text-gray-700 font-semibold rounded-xl hover:bg-gray-50 transition-colors"
                  >
                    Cancelar
                  </button>
                  <button
                    onClick={handleAddRecurringExpenses}
                    disabled={selectedTemplates.length === 0}
                    className="flex-1 px-6 py-3 bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800 text-white font-bold rounded-xl shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Agregar {selectedTemplates.length > 0 && `(${selectedTemplates.length})`}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default MonthDetail;
